import { createSelector } from "reselect"
import { depsArrToPosStr } from "../../functions/nodes/nodesDependencies"
import { dateDurationAdd } from "../../functions/timeHandler/dateDurationAdd"
import { isoDateOneHourAdd } from "../../functions/nodes/nodesAccumulations2"
import { durToDurStr } from "../../functions/timeHandler/durations"
import { DAY_START, DAY_END } from "../../const/globals"
import parseISO from "date-fns/parseISO"
import isEqual from "date-fns/isEqual"
import isAfter from "date-fns/isAfter"
import isBefore from "date-fns/isBefore"

/**
 * v1.0.0: (c) Prof. Dr. Ulrich Anders
 *
 * Gets nodes from current status
 * @param {object} state
 * @returns {object} nodes
 */
const getNodesCurrent = (state) =>
  state.project.statuses[state.project.statusCurrent].nodes

/**
 * v1.0.0: (c) Prof. Dr. Ulrich Anders
 *
 * Gets rows from current status
 * @param {object} state
 * @returns {array} rows
 */
const getRowsCurrent = (state) =>
  state.project.statuses[state.project.statusCurrent].rows

/**
 * v1.0.0: (c) Prof. Dr. Ulrich Anders
 *
 * Gets current status from state
 * @param {object} state
 * @returns {object} statusCurrent
 */
const getStatusCurrent = (state) => state.project.statusCurrent

/**
 * v1.0.0: (c) Prof. Dr. Ulrich Anders
 *
 * Generates extra infos for nodes from current status nodes
 * by help of a selector function from reselect
 * @param {array} [getNodesCurrent, getRowsCurrent, getStatusCurrent]
 * @param {function} selector function
 * @returns {} statusCurrent
 */
const getNodesExtraInfos = createSelector(
  [getNodesCurrent, getRowsCurrent, getStatusCurrent],
  (nodesCurrent, rowsCurrent, statusCurrent) => {
    let levelMax = Math.max(
      ...rowsCurrent.map((nId) => nodesCurrent[nId].position.length)
    )

    let nodesExtraInfos = {}
    for (let [key] of Object.entries(nodesCurrent)) {
      const fromWhenDate = parseISO(nodesCurrent[key].fromWhen)
      const byWhenDate = parseISO(nodesCurrent[key].byWhen)

      let toProjectionDate = dateDurationAdd(
        fromWhenDate,
        nodesCurrent[key].projection,
        DAY_START,
        DAY_END
      )

      let toProjectionISO = toProjectionDate.toISOString()

      let fromSlackISO = isoDateOneHourAdd(
        toProjectionISO,
        DAY_START,
        DAY_END
      ).toISOString()

      // toProjectionISO or fromSlackIOS cannot be after byWhen
      if (
        isEqual(toProjectionDate, byWhenDate) ||
        isAfter(toProjectionDate, byWhenDate)
      ) {
        toProjectionISO = nodesCurrent[key].byWhen
        // TODO: check logic
        // one day + 1 hour behind byWhen to move out of the way
        let fromSlackDate = dateDurationAdd(
          toProjectionDate,
          { days: 1, hours: 1 },
          DAY_START,
          DAY_END
        )
        fromSlackISO = fromSlackDate.toISOString()
      }

      const spanStr = durToDurStr(nodesCurrent[key].span)
      const spentStr = durToDurStr(nodesCurrent[key].spent)
      const slackStr = durToDurStr(nodesCurrent[key].slack)
      const projectionStr = durToDurStr(nodesCurrent[key].projection)

      const isDeadlineDue = isBefore(
        parseISO(nodesCurrent[key].byWhen),
        parseISO(statusCurrent)
      )

      const isDelivered = nodesCurrent[key].degree === 100

      const isCommentRequired =
        nodesCurrent[key].children.length > 0
          ? false
          : (nodesCurrent[key].deadline > 0 &&
              nodesCurrent[key].deadline < 4) ||
            (nodesCurrent[key].forecast > 0 &&
              nodesCurrent[key].forecast < 4) ||
            (nodesCurrent[key].quality > 0 && nodesCurrent[key].quality < 4)

      nodesExtraInfos[key] = {
        levelMax,
        precedentsStr: depsArrToPosStr(
          nodesCurrent,
          nodesCurrent[key].precedents
        ),
        spanStr,
        spentStr,
        toProjectionISO,
        projectionStr,
        fromSlackISO,
        slackStr,
        isDeadlineDue,
        isDelivered,
        isCommentRequired,
      }
    }
    return nodesExtraInfos
  }
)

export default getNodesExtraInfos
